import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, FormGroup, Input, Label } from 'reactstrap'
import CurrencyFormat from 'react-currency-format'
import toastr from 'toastr'
import { useHistory } from 'react-router-dom'

import Modal from '../../../components/Modal'
import ApiService from '../../../data/services/ApiService'

const getOutstanding = (invoice = {}) => {
    if (invoice.saldoPendiente !== null && invoice.saldoPendiente !== undefined) {
        return parseFloat(invoice.saldoPendiente) || 0
    }
    return parseFloat(invoice.total) || 0
}

const today = () => {
    const date = new Date()
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    return localDate.toISOString().slice(0, 10)
}

const RegistrarPagoModal = ({ invoice, isOpen, onRequestClose, onSuccess }) => {
    const history = useHistory()
    const [fechaPago, setFechaPago] = useState(today())
    const [xmlFile, setXmlFile] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [motivo, setMotivo] = useState('')
    const [confirmedExternal, setConfirmedExternal] = useState(false)
    const [complementUuid, setComplementUuid] = useState('')
    const [showXmlUpload, setShowXmlUpload] = useState(false)
    const activeManualPayment = useMemo(
        () => [
            ...(invoice?.pagosManualesPue || []),
            ...(invoice?.pagosManualesExternos || [])
        ].find(payment => payment.status === 'activo'),
        [invoice]
    )

    useEffect(() => {
        if (!isOpen) return
        setFechaPago(today())
        setXmlFile(null)
        setMotivo('')
        setConfirmedExternal(false)
        setComplementUuid('')
        setShowXmlUpload(false)
    }, [isOpen, invoice?._id])

    if (!invoice) return null
    const outstanding = getOutstanding(invoice)

    const handlePueSubmit = async () => {
        try {
            setSubmitting(true)
            const response = await ApiService.registrarPagoPue(invoice._id, {
                fechaPago,
                formaPago: invoice.formaPago,
                monto: outstanding
            })
            toastr.success(response.data.message)
            onSuccess?.(response.data.data)
            onRequestClose()
        } catch (error) {
            toastr.error(error.response?.data?.message || 'No pude registrar el pago.')
        } finally {
            setSubmitting(false)
        }
    }

    const handlePpdSubmit = async () => {
        if (!confirmedExternal) {
            toastr.error('Confirma que el complemento ya fue emitido fuera de Timbrat.')
            return
        }
        try {
            setSubmitting(true)
            let response
            if (xmlFile) {
                const data = new FormData()
                data.append('xml', xmlFile)
                data.append('facturaId', invoice._id)
                response = await ApiService.importarComplemento(data)
            } else {
                response = await ApiService.registrarPagoExterno(invoice._id, {
                    fechaPago,
                    monto: outstanding,
                    confirmado: true,
                    complementoUuid: complementUuid.trim() || undefined
                })
            }
            toastr.success(response.data.message)
            onSuccess?.(response.data.data)
            onRequestClose()
        } catch (error) {
            toastr.error(error.response?.data?.message || 'No pude marcar la factura como pagada.')
        } finally {
            setSubmitting(false)
        }
    }

    const handleCreateComplement = () => {
        onRequestClose()
        history.push('/complementos/nuevo', { facturaId: invoice._id })
    }

    const handleReverse = async () => {
        if (!activeManualPayment || motivo.trim().length < 3) {
            toastr.error('Indica el motivo de la reversión.')
            return
        }
        try {
            setSubmitting(true)
            const response = await ApiService.revertirPagoManual(invoice._id, activeManualPayment._id, motivo)
            toastr.success(response.data.message)
            onSuccess?.(response.data.data)
            onRequestClose()
        } catch (error) {
            toastr.error(error.response?.data?.message || 'No pude revertir el pago.')
        } finally {
            setSubmitting(false)
        }
    }

    let body
    let footer
    if (activeManualPayment) {
        body = (
            <>
                <Alert color="warning">La reversión quedará registrada en la bitácora y volverá a abrir el saldo por cobrar.</Alert>
                <FormGroup>
                    <Label>Motivo de la reversión</Label>
                    <Input value={motivo} onChange={event => setMotivo(event.target.value)} maxLength={300} />
                </FormGroup>
            </>
        )
        footer = (
            <>
                <Button color="secondary" onClick={onRequestClose}>Cerrar</Button>
                <Button color="danger" disabled={submitting} onClick={handleReverse}>{submitting ? 'Revirtiendo…' : 'Revertir pago'}</Button>
            </>
        )
    } else if (invoice.metodoPago === 'PUE') {
        body = (
            <>
                <Alert color="info">
                    PUE no requiere complemento únicamente cuando el total se recibió con la forma indicada y a más tardar el último día del mes de emisión.
                </Alert>
                <FormGroup>
                    <Label htmlFor="pue-payment-date">Fecha real del pago</Label>
                    <Input id="pue-payment-date" type="date" value={fechaPago} onChange={event => setFechaPago(event.target.value)} />
                </FormGroup>
                <div style={{ marginBottom: 10 }}><strong>Monto total:</strong>{' '}
                    <CurrencyFormat value={outstanding} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                </div>
                <div><strong>Forma de pago del CFDI:</strong> {invoice.formaPago || 'Sin clave'}</div>
            </>
        )
        footer = (
            <>
                <Button color="secondary" onClick={onRequestClose}>Cancelar</Button>
                <Button color="primary" disabled={submitting || !fechaPago} onClick={handlePueSubmit}>{submitting ? 'Registrando…' : 'Registrar pago total'}</Button>
            </>
        )
    } else {
        body = (
            <>
                <p className="text-muted">
                    Registra el cobro si el complemento ya fue emitido en otro sistema. Esto actualiza el saldo en Timbrat; no modifica el CFDI.
                </p>
                <FormGroup>
                    <Label htmlFor="external-payment-date">Fecha real del pago</Label>
                    <Input id="external-payment-date" type="date" value={fechaPago} max={today()} onChange={event => setFechaPago(event.target.value)} />
                </FormGroup>
                <div style={{ marginBottom: 16 }}><strong>Saldo a marcar como pagado:</strong>{' '}
                    <CurrencyFormat value={outstanding} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                </div>
                <FormGroup check style={{ marginBottom: 16 }}>
                    <Label check>
                        <Input
                            type="checkbox"
                            checked={confirmedExternal}
                            onChange={event => setConfirmedExternal(event.target.checked)}
                        />{' '}
                        Confirmo que el complemento de pago ya fue emitido fuera de Timbrat.
                    </Label>
                </FormGroup>
                <FormGroup>
                    <Label htmlFor="external-complement-uuid">UUID del complemento <span className="text-muted">(opcional)</span></Label>
                    <Input
                        id="external-complement-uuid"
                        value={complementUuid}
                        onChange={event => setComplementUuid(event.target.value)}
                        placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
                        maxLength={36}
                    />
                </FormGroup>
                {!showXmlUpload ? (
                    <Button color="link" className="p-0" onClick={() => setShowXmlUpload(true)}>
                        Adjuntar XML del complemento (opcional)
                    </Button>
                ) : (
                    <FormGroup>
                        <Label htmlFor="external-complement-xml">XML del complemento <span className="text-muted">(opcional)</span></Label>
                        <Input id="external-complement-xml" type="file" accept=".xml,text/xml,application/xml" onChange={event => setXmlFile(event.target.files?.[0] || null)} />
                        <small className="form-text text-muted">Si lo adjuntas, Timbrat verificará que esté vigente y que liquide esta factura.</small>
                    </FormGroup>
                )}
                <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid #e9ecef' }}>
                    <span className="text-muted">¿Todavía no tienes complemento? </span>
                    <Button color="link" className="p-0 align-baseline" onClick={handleCreateComplement}>
                        Crearlo en Timbrat
                    </Button>
                </div>
            </>
        )
        footer = (
            <>
                <Button color="secondary" onClick={onRequestClose}>Cancelar</Button>
                <Button color="primary" disabled={submitting || !fechaPago || !confirmedExternal} onClick={handlePpdSubmit}>
                    {submitting ? (xmlFile ? 'Verificando…' : 'Registrando…') : 'Marcar como pagada'}
                </Button>
            </>
        )
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            title={activeManualPayment ? 'Revertir pago registrado' : `Marcar factura como pagada${invoice.folio ? ` · ${invoice.folio}` : ''}`}
            contentLabel="Registrar pago de factura"
            body={body}
            footer={footer}
            styles={{ content: { width: 'min(560px, 94vw)' } }}
        />
    )
}

export default RegistrarPagoModal
